import fs from 'fs'
import path from 'path'
import Head from 'next/head'
import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next'
import matter from 'gray-matter'
import renderToString from 'next-mdx-remote/render-to-string'
import { postFilePaths, POSTS_PATH } from 'utils/mdxUtils'
import { Container, Link } from '@doist/marketist'
import smartypants from '@silvenon/remark-smartypants'
import mdxPrism from 'mdx-prism'
import { MdxRemote } from 'next-mdx-remote/types'
import * as Sections from 'components/posts'
import rehypeSlug from 'rehype-slug'

// Custom components/renderers to pass to MDX.
// Since the MDX files aren't loaded by webpack, they have no knowledge of how
// to handle import statements. Instead, you must include components in scope
// here.
const components = {
    a: Link,
    Head,
}

interface PostPageProps {
    frontMatter: Metadata
    source: MdxRemote.Source
}

export default function PostsPage({ source, frontMatter }: PostPageProps): JSX.Element {
    return (
        <>
            <Head>
                <title>{frontMatter.title}</title>
                <meta name="description" content={frontMatter.description} />
                <meta property="og:title" content={frontMatter.title} />
                <meta property="og:description" content={frontMatter.description} />
                <meta property="og:image" content="https://doist.dev/og-image.png" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={frontMatter.title} />
                <meta name="twitter:description" content={frontMatter.description} />
                <meta name="twitter:image" content="https://doist.dev/og-image.png" />
                <link
                    rel="stylesheet"
                    href="https://cdnjs.cloudflare.com/ajax/libs/prism/1.16.0/themes/prism-tomorrow.min.css"
                    crossOrigin="anonymous"
                />
            </Head>

            <Container tag="main" heroSection>
                <Sections.Heading data={frontMatter} />
                <Sections.MainContent content={source.renderedOutput} />
            </Container>
        </>
    )
}

export async function getStaticProps({
    params,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<PostPageProps>> {
    const postFilePath = path.join(POSTS_PATH, `${params.slug}.mdx`)
    const source = fs.readFileSync(postFilePath)
    const { content, data } = matter(source)
    const mdxSource = await renderToString(content, {
        components,
        // Optionally pass remark/rehype plugins
        mdxOptions: {
            remarkPlugins: [smartypants],
            rehypePlugins: [rehypeSlug, mdxPrism],
        },
        scope: data,
    })

    return {
        props: {
            source: mdxSource,
            frontMatter: data as Metadata,
        },
    }
}

/** Generates all the static pages */
export async function getStaticPaths(): Promise<GetStaticPathsResult> {
    const paths = postFilePaths
        .map((path) => path.replace(/\.mdx?$/, ''))
        .map((slug) => ({ params: { slug } }))

    return { paths, fallback: false }
}
